/**
 * Cache Read Model facade (ADR-0014 / Runtime Architecture §4).
 *
 * Public contract over InstantCache:
 *   getReadModel(name) → snapshot | null
 *   markDirty(name | name[])
 *   ensureWarm(name | name[], options?)
 *   invalidateForDomainEvent(eventKey)
 *   isReadModelReady() + typed getters (Phase 7)
 *
 * UI / pages / feature query hooks should import this module — not InstantCache
 * internals (preCalculate*, cache.get, getFiltered*).
 */

import instantCache from './instantCache';

/** @typedef {keyof typeof READ_MODELS | typeof READ_MODELS[keyof typeof READ_MODELS]} ReadModelName */

export const READ_MODELS = Object.freeze({
  STOCK: 'stock',
  INVENTORY_METRICS: 'inventoryMetrics',
  MANAGER_DASHBOARD: 'managerDashboard',
  WORKER_DASHBOARD: 'workerDashboard',
  POS_AVAILABILITY: 'posAvailability',
  SALES_LIST: 'salesList',
  PURCHASES: 'purchases',
  REPORTS: 'reports',
  EXPENSES: 'expenses',
  FINANCIAL_METRICS: 'financialMetrics'
});

/**
 * Large history arrays — DataContext / entity store is SSOT.
 * Never keep a second copy in InstantCache across routes (heap + inventaire jank).
 * Pass them only via ensureWarm({ data }) for one-shot aggregate calc.
 */
export const EPHEMERAL_ENTITY_KEYS = Object.freeze([
  'sales',
  'orders',
  'invoices',
  'invoicePayments',
  'supermarketInvoices',
  'supermarketPayments',
  'supplierDebts'
]);

const EPHEMERAL_ENTITY_KEY_SET = new Set(EPHEMERAL_ENTITY_KEYS);

function stripEphemeralEntityKeys(partial) {
  if (!partial || typeof partial !== 'object') return partial;
  const out = { ...partial };
  for (const key of EPHEMERAL_ENTITY_KEY_SET) {
    delete out[key];
  }
  return out;
}

/**
 * Domain fact → finite dirty set. No event means "everything dirty".
 * Keys are stable logical names (not necessarily DOMAIN_EVENTS string values).
 */
export const DOMAIN_EVENT_DIRTY_MAP = Object.freeze({
  goods_received: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY,
    READ_MODELS.PURCHASES
  ],
  lot_created: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY
  ],
  lot_consumed: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY,
    READ_MODELS.SALES_LIST
  ],
  sale_completed: [
    READ_MODELS.STOCK,
    READ_MODELS.POS_AVAILABILITY,
    READ_MODELS.SALES_LIST,
    READ_MODELS.MANAGER_DASHBOARD
  ],
  purchase_received: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY,
    READ_MODELS.PURCHASES
  ],
  expense_recorded: [
    READ_MODELS.EXPENSES,
    READ_MODELS.FINANCIAL_METRICS,
    READ_MODELS.MANAGER_DASHBOARD,
    READ_MODELS.REPORTS
  ],
  supplier_payment_recorded: [
    READ_MODELS.FINANCIAL_METRICS,
    READ_MODELS.MANAGER_DASHBOARD,
    READ_MODELS.REPORTS
  ],
  // Invoices: finance metrics / reports only — not manager dashboard (avoids re-warm on hydrate)
  supplier_invoice_changed: [
    READ_MODELS.FINANCIAL_METRICS,
    READ_MODELS.REPORTS
  ],
  customer_balance_changed: [],
  stock_transferred: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY
  ],
  lot_updated: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY
  ],
  expiry_processed: [
    READ_MODELS.STOCK,
    READ_MODELS.INVENTORY_METRICS,
    READ_MODELS.POS_AVAILABILITY
  ]
});

const dirtyModels = new Set();

function asList(nameOrNames) {
  if (nameOrNames == null) return [];
  return Array.isArray(nameOrNames) ? nameOrNames.filter(Boolean) : [nameOrNames];
}

function buildWarmPayload(overrides = {}) {
  if (!instantCache?.isReady?.()) return null;
  const cache = instantCache.cache;
  return {
    products: overrides.products ?? cache.get('products') ?? [],
    lots: overrides.lots ?? cache.get('lots') ?? {},
    sales: overrides.sales ?? cache.get('sales') ?? [],
    purchases: overrides.purchases ?? cache.get('purchases') ?? [],
    expenses: overrides.expenses ?? cache.get('expenses') ?? [],
    customers: overrides.customers ?? cache.get('customers') ?? [],
    discrepancyManagement:
      overrides.discrepancyManagement ??
      cache.get('discrepancyManagement') ?? {
        selfDeviations: [],
        handoverDeviations: []
      },
    stockLocations: overrides.stockLocations ?? instantCache.getStockLocations?.() ?? ['Principal'],
    expiredProducts: overrides.expiredProducts ?? cache.get('expiredProducts') ?? [],
    stockSnapshots:
      overrides.stockSnapshots ??
      cache.get('stockSnapshots') ??
      null,
    // Finance entities — Manager warm keys already pass these; keep them on the payload
    supermarketPayments:
      overrides.supermarketPayments ?? cache.get('supermarketPayments') ?? [],
    supermarketInvoices:
      overrides.supermarketInvoices ?? cache.get('supermarketInvoices') ?? [],
    invoicePayments: overrides.invoicePayments ?? cache.get('invoicePayments') ?? [],
    supplierDebts: overrides.supplierDebts ?? cache.get('supplierDebts') ?? []
  };
}

/**
 * @param {string} name
 * @param {string} [key] reserved for scoped keys (unused in Phase 3)
 * @returns {unknown|null}
 */
export function getReadModel(name, key) {
  void key;
  if (!instantCache?.isReady?.()) return null;

  switch (name) {
    case READ_MODELS.STOCK:
      return instantCache.cache.get('stockData') ?? null;
    case READ_MODELS.INVENTORY_METRICS:
      return instantCache.getInventoryMetrics?.() ?? instantCache.cache.get('inventoryMetrics') ?? null;
    case READ_MODELS.MANAGER_DASHBOARD:
      return instantCache.getManagerDashboard?.() ?? null;
    case READ_MODELS.WORKER_DASHBOARD:
      return instantCache.getWorkerDashboard?.() ?? instantCache.cache.get('workerDashboard') ?? null;
    case READ_MODELS.POS_AVAILABILITY:
      return instantCache.getVisibleStockProducts?.() ?? null;
    case READ_MODELS.REPORTS:
      return {
        stockMovement: instantCache.getStockMovementData?.() ?? null,
        expirationAlerts: instantCache.getExpirationAlerts?.() ?? null,
        expiredProducts: instantCache.getExpiredProducts?.() ?? null,
        achatVsVentePeriods: instantCache.getAchatVsVentePeriods?.() ?? null
      };
    case READ_MODELS.SALES_LIST:
      // Sales live in DataContext — InstantCache must not retain the history array.
      return null;
    case READ_MODELS.PURCHASES:
      return instantCache.cache.get('purchases') ?? null;
    case READ_MODELS.EXPENSES:
      return instantCache.cache.get('expenses') ?? null;
    case READ_MODELS.FINANCIAL_METRICS:
      return instantCache.cache.get('financialMetrics') ?? null;
    default:
      return instantCache.cache.get(name) ?? null;
  }
}

/**
 * @param {string|string[]} nameOrNames
 * @param {string} [scope] reserved
 */
export function markDirty(nameOrNames, scope) {
  void scope;
  for (const name of asList(nameOrNames)) {
    dirtyModels.add(name);
  }
}

export function isDirty(name) {
  return dirtyModels.has(name);
}

export function clearDirty(nameOrNames) {
  for (const name of asList(nameOrNames)) {
    dirtyModels.delete(name);
  }
}

export function getDirtyModels() {
  return Array.from(dirtyModels);
}

/**
 * Mark dirty set for a domain fact. Does not warm.
 * @param {string} eventKey key from DOMAIN_EVENT_DIRTY_MAP
 */
export function invalidateForDomainEvent(eventKey) {
  const models = DOMAIN_EVENT_DIRTY_MAP[eventKey];
  if (!models) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[cacheReadModel] Unknown domain event key "${eventKey}" — no models marked dirty (no full recalc).`
      );
    }
    return [];
  }
  markDirty(models);
  return models.slice();
}

/**
 * Warm only requested (or currently dirty) read models. Never calls preCalculateAllMetrics.
 *
 * @param {string|string[]|null} [nameOrNames] if null/empty → warm all currently dirty
 * @param {{ force?: boolean, data?: object, priority?: 'sync'|'idle', idleTimeout?: number, onDone?: (warmed: string[]) => void }} [options]
 * @returns {string[]} warmed model names (or requested names when deferred to idle)
 */
export function ensureWarm(nameOrNames = null, options = {}) {
  const {
    force = false,
    data: dataOverrides,
    priority = 'sync',
    idleTimeout = 4000,
    onDone
  } = options;
  const requested = asList(nameOrNames);
  const targets = requested.length
    ? requested
    : Array.from(dirtyModels);

  if (!targets.length) {
    onDone?.([]);
    return [];
  }

  const run = () => {
    if (!instantCache?.isReady?.()) return [];

    const data = buildWarmPayload(dataOverrides);
    if (!data) return [];

    const warmed = [];
    let stockCalculated = false;
    let managerCalculated = false;

    for (const name of targets) {
      if (!force && requested.length === 0 && !dirtyModels.has(name)) continue;

      switch (name) {
        case READ_MODELS.STOCK:
        case READ_MODELS.POS_AVAILABILITY:
          // Both models share the same stock projection — compute once per warm pass
          if (!stockCalculated) {
            instantCache.preCalculateStockData(data);
            stockCalculated = true;
          }
          warmed.push(name);
          break;
        case READ_MODELS.INVENTORY_METRICS: {
          const metrics = instantCache.calculateInventoryMetrics(
            data.products,
            data.lots,
            data.sales,
            data.purchases,
            data.stockSnapshots
          );
          instantCache.cache.set('inventoryMetrics', metrics);
          warmed.push(name);
          break;
        }
        case READ_MODELS.MANAGER_DASHBOARD:
          if (!managerCalculated) {
            instantCache.preCalculateManagerDashboard(data);
            managerCalculated = true;
          }
          warmed.push(name);
          break;
        case READ_MODELS.WORKER_DASHBOARD:
          if (typeof instantCache.preCalculateWorkerDashboard === 'function') {
            instantCache.preCalculateWorkerDashboard(data);
          }
          warmed.push(name);
          break;
        case READ_MODELS.REPORTS:
          if (typeof instantCache.preCalculateReportsData === 'function') {
            instantCache.preCalculateReportsData(data);
          }
          warmed.push(name);
          break;
        case READ_MODELS.FINANCIAL_METRICS:
          // Manager dashboard already covers period KPIs — do not recompute twice in one warm.
          if (!managerCalculated && typeof instantCache.preCalculateManagerDashboard === 'function') {
            instantCache.preCalculateManagerDashboard(data);
            managerCalculated = true;
          }
          warmed.push(name);
          break;
        case READ_MODELS.SALES_LIST:
        case READ_MODELS.PURCHASES:
        case READ_MODELS.EXPENSES:
          // Entity lists live in React/DataContext — InstantCache does not retain sales history.
          warmed.push(name);
          break;
        default:
          break;
      }

      dirtyModels.delete(name);
    }

    // Warm may have used dataOverrides.sales etc. — never leave them pinned in InstantCache.
    for (const key of EPHEMERAL_ENTITY_KEYS) {
      instantCache.cache.delete(key);
    }

    return warmed;
  };

  if (priority === 'idle' && typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => {
      const warmed = run();
      try {
        onDone?.(warmed);
      } catch {
        /* UI callback must not break warm */
      }
    }, { timeout: Math.max(500, Number(idleTimeout) || 4000) });
    return targets.slice();
  }

  const warmed = run();
  onDone?.(warmed);
  return warmed;
}

/**
 * Warm stock projection + inventory metrics after lot movement.
 * Orchestrator must call this instead of InstantCache internals (ADR-0014).
 * @returns {boolean} true when cache was ready and warm ran
 */
export function warmStockProjectionAfterMovement({
  lots = [],
  products = [],
  purchases = [],
  pricingProductIds = [],
  forceResync = false,
  stockSnapshots = null
} = {}) {
  if (!isReadModelReady()) return false;

  const lotsArr = Array.isArray(lots) ? lots : [];
  const purchasesArr = Array.isArray(purchases) ? purchases : [];
  const productsArr = Array.isArray(products) ? products : [];

  if (forceResync && typeof instantCache.invalidateStockQuantityCache === 'function') {
    instantCache.invalidateStockQuantityCache();
  }

  instantCache.cache.set('lots', lotsArr);
  if (purchasesArr.length) {
    instantCache.cache.set('purchases', purchasesArr);
  }

  const stockLocations = instantCache.getStockLocations?.() || ['Principal'];
  const cacheProducts =
    productsArr.length && typeof instantCache.enrichProductsWithStockData === 'function'
      ? instantCache.enrichProductsWithStockData(
          productsArr,
          lotsArr,
          purchasesArr,
          stockLocations
        )
      : productsArr;
  if (cacheProducts.length) {
    instantCache.cache.set('products', cacheProducts);
  }

  const cachePurchases = instantCache.cache.get('purchases') || purchasesArr;
  // Sales stay ephemeral — aggregates use empty list unless ensureWarm({ data }) passes them
  const cacheSales = [];

  // Resolve snapshots: explicit arg or blob (ADR-0016 Phase 3 metrics)
  let snaps = stockSnapshots;
  if (snaps == null || (Array.isArray(snaps) && snaps.length === 0)) {
    const cached = instantCache.cache.get('stockSnapshots');
    if (Array.isArray(cached) && cached.length) snaps = cached;
  }
  if (Array.isArray(snaps) && snaps.length) {
    instantCache.cache.set('stockSnapshots', snaps);
  }

  instantCache.preCalculateStockData({
    products: cacheProducts,
    lots: lotsArr,
    purchases: cachePurchases,
    stockLocations
  });

  const inventoryMetrics = instantCache.calculateInventoryMetrics(
    cacheProducts,
    lotsArr,
    cacheSales,
    cachePurchases,
    snaps
  );
  instantCache.cache.set('inventoryMetrics', inventoryMetrics);

  if (
    (pricingProductIds.length || forceResync) &&
    typeof instantCache._refreshProductPricingAfterDataChange === 'function'
  ) {
    instantCache._refreshProductPricingAfterDataChange(
      lotsArr,
      cachePurchases,
      pricingProductIds.length ? pricingProductIds : null
    );
  }

  return true;
}

/** Lots snapshot for POS filters when caller did not pass lots (facade, not InstantCache). */
export function getCachedLots() {
  if (!isReadModelReady()) return [];
  const lots = instantCache.cache?.get?.('lots');
  if (Array.isArray(lots)) return lots;
  if (lots && typeof lots === 'object') return Object.values(lots);
  return [];
}

/**
 * After purchase/lot movement: mark dirty; force-warm only when caller did not already warm.
 * Prefer invalidate-only when orchestrator set stockWarmed (see stockRecalculationOrchestrator).
 */
export function afterStockMovementCommit(eventKey = 'lot_updated', options = {}) {
  invalidateForDomainEvent(eventKey);
  if (options.skipWarm) {
    return getDirtyModels();
  }
  return ensureWarm([READ_MODELS.STOCK, READ_MODELS.INVENTORY_METRICS, READ_MODELS.POS_AVAILABILITY], {
    force: options.force !== false,
    priority: options.priority
  });
}

/**
 * After sale: mark dirty; stock already updated atomically — notify UI.
 * Do NOT ensureWarm manager/inventory here — POS checkout must stay snappy;
 * NewManagerDashboard warms on idle when that surface is mounted.
 */
export function afterSaleCommit() {
  invalidateForDomainEvent('sale_completed');
  return getDirtyModels();
}

/**
 * After purchase receive: stock/inventory already via orchestrator — do not AllMetrics.
 */
export function afterPurchaseCommit() {
  invalidateForDomainEvent('purchase_received');
  // Orchestrator already warmed stock+inventory; manager only if user opens that surface later.
  markDirty(READ_MODELS.MANAGER_DASHBOARD);
  markDirty(READ_MODELS.REPORTS);
  markDirty(READ_MODELS.FINANCIAL_METRICS);
  return getDirtyModels();
}

/**
 * After supplier payment or invoice change — mark finance dirty; warm manager idle only.
 * Avoid force-warming Reports + double Manager on every Enregistrer.
 */
export function afterSupplierFinanceCommit(eventKey = 'supplier_payment_recorded') {
  invalidateForDomainEvent(eventKey);
  ensureWarm([READ_MODELS.MANAGER_DASHBOARD], { priority: 'idle' });
  return getDirtyModels();
}

/**
 * After expense create/update — mark finance dirty; warm manager idle only.
 * Do not force Reports/AllMetrics on the Enregistrer path (same idea as afterSaleCommit).
 */
export function afterExpenseCommit(eventKey = 'expense_recorded') {
  invalidateForDomainEvent(eventKey);
  markDirty(READ_MODELS.REPORTS);
  ensureWarm([READ_MODELS.MANAGER_DASHBOARD], { priority: 'idle' });
  return getDirtyModels();
}

// —— Phase 7: typed read helpers (UI must not call InstantCache for these) ——

export function isReadModelReady() {
  return Boolean(instantCache?.isReady?.());
}

/**
 * Sync entity snapshots into the cache layer.
 * Default is silent (no reports/manager/worker recalc) — callers that need
 * warm use ensureWarm separately.
 *   { recalc: true }  → debounced updatePartialData (legacy full update)
 *   { applyNow: true } → applyPartialDataNow (flush pending immediately)
 */
export function syncEntities(partial, options = {}) {
  if (!partial || !instantCache) return;
  // Default: never mirror sales/orders/invoice history into InstantCache.
  // { keepEphemeral: true } only for rare legacy paths that still need a temporary clone.
  const toApply =
    options.keepEphemeral === true ? partial : stripEphemeralEntityKeys(partial);
  if (options.applyNow && typeof instantCache.applyPartialDataNow === 'function') {
    instantCache.applyPartialDataNow(toApply);
    return;
  }
  if (options.recalc && typeof instantCache.updatePartialData === 'function') {
    instantCache.updatePartialData(toApply);
    return;
  }
  // Silent snapshot write — avoids double/triple dashboard calc with ensureWarm
  if (!instantCache.cache?.set) return;
  for (const [key, value] of Object.entries(toApply)) {
    if (value !== undefined) {
      instantCache.cache.set(key, value);
    }
  }
  // Drop any prior ephemeral clones if caller still sent them (stripped above).
  if (options.keepEphemeral !== true) {
    for (const key of EPHEMERAL_ENTITY_KEYS) {
      if (Object.prototype.hasOwnProperty.call(partial, key)) {
        instantCache.cache.delete(key);
      }
    }
  }
}

export function setEntitySnapshot(key, value) {
  if (!instantCache?.cache?.set || key == null) return;
  if (EPHEMERAL_ENTITY_KEY_SET.has(key)) {
    if (key === 'sales' && Array.isArray(value)) {
      instantCache.cache.set('salesCount', value.length);
    }
    instantCache.cache.delete(key);
    return;
  }
  instantCache.cache.set(key, value);
}

/** Drop stale sales filter keys after list sync (Sales page). */
export function invalidateSalesFilterKeys() {
  if (!instantCache?.cache?.keys) return;
  for (const key of Array.from(instantCache.cache.keys())) {
    if (String(key).startsWith('filter_sales')) {
      instantCache.cache.delete(key);
    }
  }
}

/**
 * Purchases snapshot sync.
 * Default: analytics/index warm only (pages).
 * `{ commit: true }`: full write-path update (purchases + stock when not coordinated).
 */
export function syncPurchasesSnapshot(purchases, options = {}) {
  if (!Array.isArray(purchases) || !instantCache) return;
  if (options.commit && typeof instantCache.updatePurchasesData === 'function') {
    instantCache.updatePurchasesData(purchases);
    return;
  }
  if (!instantCache.cache?.set) return;
  instantCache.cache.set('purchases', purchases);
  instantCache.cache.set('purchasesCount', purchases.length);
  if (!isReadModelReady()) return;
  try {
    instantCache.preCalculatePurchasesData?.({
      purchases,
      suppliers: instantCache.cache.get('suppliers') || [],
      products: instantCache.cache.get('products') || [],
      lots: instantCache.cache.get('lots') || {}
    });
  } catch {
    /* best-effort */
  }
}

/** Products write — keep filter_ + legacy productsCacheUpdated via updateProductsData. */
export function syncProductsSnapshot(products) {
  if (!Array.isArray(products) || !instantCache) return;
  if (isReadModelReady() && typeof instantCache.updateProductsData === 'function') {
    instantCache.updateProductsData(products);
    return;
  }
  if (!instantCache.cache?.set) return;
  instantCache.cache.set('products', products);
  instantCache.cache.set('productsCount', products.length);
}

/** Suppliers write — refresh purchase search/filters + suppliersCacheUpdated. */
export function syncSuppliersSnapshot(suppliers) {
  if (!Array.isArray(suppliers) || !instantCache) return;
  if (isReadModelReady() && typeof instantCache.updateSuppliersData === 'function') {
    instantCache.updateSuppliersData(suppliers);
    return;
  }
  if (!instantCache.cache?.set) return;
  instantCache.cache.set('suppliers', suppliers);
}

/**
 * Sales list sync (Sales page / POS / refresh).
 *   mode: 'full' (default) → updateSalesData (light analytics; worker only if warmWorker)
 *   mode: 'silent' → count + invalidate filters; idle light analytics (no raw sales retained)
 *
 * InstantCache keeps derived KPIs only — never the full sales history array.
 */
export function syncSalesSnapshot(sales, options = {}) {
  if (!Array.isArray(sales) || !instantCache?.cache?.set) return;
  const mode = options.mode === 'silent' ? 'silent' : 'full';
  const warmWorker = options.warmWorker === true;
  const ready = Boolean(instantCache.isInitialized || instantCache.isReady?.());

  instantCache.cache.set('salesCount', sales.length);
  // Never retain raw history — DataContext / entity store owns the list.
  instantCache.cache.delete('sales');

  if (!ready) {
    return;
  }

  if (mode === 'silent') {
    invalidateSalesFilterKeys();
    const schedule =
      typeof requestIdleCallback === 'function'
        ? (fn) => requestIdleCallback(fn, { timeout: 2000 })
        : (fn) => setTimeout(fn, 0);
    schedule(() => {
      try {
        instantCache.updateSalesData?.(sales, { warmWorker: false, lightAnalytics: true });
      } catch {
        /* best-effort */
      }
    });
    return;
  }

  if (typeof instantCache.updateSalesData === 'function') {
    instantCache.updateSalesData(sales, { warmWorker, lightAnalytics: !warmWorker });
  }
}

/** Expired products + alerts warm after KV refresh. */
export function syncExpiredProductsReadModel({ expiredProducts, lots, products } = {}) {
  if (!isReadModelReady()) return;
  try {
    instantCache.preCalculateExpiredProducts?.({
      expiredProducts,
      lots,
      products
    });
    instantCache.preCalculateExpirationAlerts?.({
      lots,
      products
    });
  } catch {
    /* best-effort */
  }
}

/** UI bridge after silent snapshot writes (drafts, etc.). */
export function notifyReadModelUpdate() {
  try {
    instantCache?.notifyCacheUpdate?.();
  } catch {
    /* optional */
  }
}

export function getFilteredProducts(filters = {}) {
  if (!isReadModelReady()) return null;
  return instantCache.getFilteredProducts?.(filters) ?? null;
}

export function getFilteredStockData(filters = {}) {
  if (!isReadModelReady()) return [];
  return instantCache.getFilteredStockData?.(filters) ?? [];
}

/** True when Stock RM should be warmed — does not recalculate. */
export function isStockProjectionStale() {
  if (!isReadModelReady()) return true;
  if (typeof instantCache.isStockProjectionStale === 'function') {
    return Boolean(instantCache.isStockProjectionStale());
  }
  const rows = instantCache.cache?.get?.('stockData');
  return !Array.isArray(rows) || rows.length === 0;
}

export function getVisibleStockProducts() {
  if (!isReadModelReady()) return [];
  try {
    return instantCache.getVisibleStockProducts?.() ?? [];
  } catch {
    return [];
  }
}

export function getStockLocations() {
  if (!isReadModelReady()) return ['Principal'];
  const locations = instantCache.getStockLocations?.();
  return Array.isArray(locations) && locations.length > 0 ? locations : ['Principal'];
}

export function refreshStockLocations(opts) {
  if (typeof instantCache?.refreshStockLocations === 'function') {
    instantCache.refreshStockLocations(opts);
  }
}

export function getStockMovementData() {
  if (!isReadModelReady()) return [];
  const data = instantCache.getStockMovementData?.();
  return Array.isArray(data) ? data : [];
}

/** True after preCalculateReportsData has written stockMovementData (even if empty catalog). */
export function hasReportsStockProjection() {
  if (!isReadModelReady()) return false;
  try {
    return instantCache.cache?.has?.('stockMovementData') === true;
  } catch {
    return false;
  }
}

export function getExpirationAlerts() {
  if (!isReadModelReady()) return [];
  return instantCache.getExpirationAlerts?.() ?? [];
}

export function getExpiredProducts() {
  if (!isReadModelReady()) return [];
  return instantCache.getExpiredProducts?.() ?? [];
}

/**
 * Plain-object product index with string + raw id keys.
 * instantCache stores a Map — callers must not use Map bracket notation.
 */
export function getProductIndex() {
  if (!isReadModelReady()) return {};
  const raw = instantCache.getProductIndex?.() ?? {};
  const idx = {};
  const add = (p) => {
    if (p == null) return;
    if (p.id != null) {
      idx[p.id] = p;
      idx[String(p.id)] = p;
    }
    if (p.productId != null) {
      idx[p.productId] = p;
      idx[String(p.productId)] = p;
    }
  };
  if (raw instanceof Map) {
    for (const p of raw.values()) add(p);
  } else if (raw && typeof raw === 'object') {
    Object.values(raw).forEach(add);
  }
  return idx;
}

export function getSalesAnalytics() {
  if (!isReadModelReady()) return null;
  return instantCache.getSalesAnalytics?.() ?? null;
}

export function getGroupedPurchases() {
  if (!isReadModelReady()) return [];
  return instantCache.getGroupedPurchases?.() ?? [];
}

export function getFilteredPurchases(filters = {}) {
  if (!isReadModelReady()) return [];
  return instantCache.getFilteredPurchases?.(filters) ?? [];
}

export function getPurchasesAnalytics() {
  if (!isReadModelReady()) return null;
  return instantCache.getPurchasesAnalytics?.() ?? null;
}

export function getCustomerSearchResults(searchTerm) {
  if (!isReadModelReady()) return [];
  return instantCache.getCustomerSearchResults?.(searchTerm) ?? [];
}

export function getDraftData(businessId) {
  if (!isReadModelReady()) return [];
  return instantCache.getDraftData?.(businessId) ?? [];
}

export function getProductValue(productId, stockLocation) {
  return instantCache.getProductValue?.(productId, stockLocation);
}

export function getLotPricingData(productId, location) {
  return instantCache.getLotPricingData?.(productId, location) ?? null;
}

export function getMostRecentSellingPrice(productId) {
  return instantCache.getProductMostRecentSellingPrice?.(productId) ?? null;
}

export function getProductDisplaySellingPrice(productId, stockLocation = 'Principal') {
  if (!isReadModelReady()) return 0;
  return (
    Number(instantCache.getProductDisplaySellingPrice?.(productId, stockLocation)) || 0
  );
}

export function getDefaultWorkerDashboard() {
  return (
    instantCache.getDefaultWorkerDashboard?.() ?? {
      totalSales: 0,
      totalRevenue: 0,
      openEcarts: [],
      sessions: []
    }
  );
}

export function getWorkerDashboard(workerId, businessId = null) {
  if (!isReadModelReady() || !workerId) return getDefaultWorkerDashboard();
  return (
    instantCache.getWorkerDashboard?.(workerId, businessId) ?? getDefaultWorkerDashboard()
  );
}

/**
 * After stock transfer — keep legacy transfer cache path behind the contract.
 */
export function syncTransfersSnapshot(transferPayload) {
  if (!transferPayload || !isReadModelReady()) return;
  try {
    instantCache.updateTransfersData?.(transferPayload);
  } catch {
    /* best-effort */
  }
}

export function calculateCartTotals(cart) {
  if (!isReadModelReady()) return null;
  return instantCache.calculateCartTotals?.(cart) ?? null;
}

export function resolveWorkerName(workerId) {
  if (!isReadModelReady()) return null;
  return instantCache.resolveWorkerName?.(workerId) ?? null;
}

const cacheReadModel = {
  READ_MODELS,
  DOMAIN_EVENT_DIRTY_MAP,
  getReadModel,
  markDirty,
  isDirty,
  clearDirty,
  getDirtyModels,
  ensureWarm,
  invalidateForDomainEvent,
  afterStockMovementCommit,
  afterSaleCommit,
  afterPurchaseCommit,
  afterSupplierFinanceCommit,
  afterExpenseCommit,
  isReadModelReady,
  warmStockProjectionAfterMovement,
  getCachedLots,
  syncEntities,
  setEntitySnapshot,
  invalidateSalesFilterKeys,
  syncPurchasesSnapshot,
  syncProductsSnapshot,
  syncSuppliersSnapshot,
  syncSalesSnapshot,
  syncExpiredProductsReadModel,
  notifyReadModelUpdate,
  getFilteredProducts,
  getFilteredStockData,
  isStockProjectionStale,
  getVisibleStockProducts,
  getStockLocations,
  refreshStockLocations,
  getStockMovementData,
  hasReportsStockProjection,
  getExpirationAlerts,
  getExpiredProducts,
  getProductIndex,
  getSalesAnalytics,
  getGroupedPurchases,
  getFilteredPurchases,
  getPurchasesAnalytics,
  getCustomerSearchResults,
  getDraftData,
  getProductValue,
  getLotPricingData,
  getMostRecentSellingPrice,
  getProductDisplaySellingPrice,
  getDefaultWorkerDashboard,
  getWorkerDashboard,
  syncTransfersSnapshot,
  calculateCartTotals,
  resolveWorkerName
};

export default cacheReadModel;
